import { TFunction } from 'i18next';
import { AddToastParams, getMessagesControl } from 'providers/Toast';
import { CostCalculationEntry, TeamService } from 'entities';
import { FormTeamService } from './types';
import { normalizeText } from 'utils/text';
import { getCalculationReference, resolveReferences, validadeCurrencyRef } from '../calculations';

export type ServiceToValidate = FormTeamService & { services: TeamService[] };

export const MESSAGES = getMessagesControl({
  notFilledServiceName: (t) => ({
    title: t('service-name-is-required'),
    type: 'info',
  }),

  hasNotCostVariable: (t) => ({
    title: t('you-need-to-provide-at-least-one-variable-when-creating-a-service'),
    type: 'info',
  }),

  notFilledCostVariableName: (t) => ({
    title: t('make-sure-to-fill-all-variable-names'),
    type: 'info',
  }),

  notFilledTagName: (t) => ({
    title: t('make-sure-to-fill-all-tag-names'),
    type: 'info',
  }),

  hasSameServiceName: (t) => ({
    title: t('the-service-name-already-exists-please-choose-another-one'),
    type: 'info',
  }),

  duplicateVariableName: (t) => ({
    title: t('service-duplicate-variable'),
    type: 'info',
  }),

  duplicateTagName: (t) => ({
    title: t('service-duplicate-tag'),
    type: 'info',
  }),

  costCalculationsEmpty: (t) => ({
    title: t('cost-calculation-empty'),
    type: 'info',
  }),

  costCalculationsEmptyRefs: (t) => ({
    title: t('cost-calculation-empty-refs'),
    type: 'info',
  }),

  costCalculationsCurrencyRef: (t) => ({
    title: t('cost-calculation-currency-ref'),
    type: 'info',
  }),

  currencyNumberSumOperations: (t) => ({
    title: t('cost-calculation-sum-ops'),
    type: 'info',
  }),
});

const validations: Array<{
  errorKey: keyof typeof MESSAGES;
  validate: (service: ServiceToValidate) => boolean;
}> = [
  {
    errorKey: 'notFilledServiceName',
    validate: ({ name }) => !name.trim(),
  },
  {
    errorKey: 'hasNotCostVariable',
    validate: ({ costVariables }) => !costVariables.length,
  },
  {
    errorKey: 'notFilledCostVariableName',
    validate: ({ costVariables }) =>
      costVariables.some((costVariable) => !costVariable.name.trim()),
  },
  {
    errorKey: 'notFilledTagName',
    validate: ({ tags }) => tags.some((tag) => !tag.name.trim()),
  },
  {
    errorKey: 'hasSameServiceName',
    validate: ({ services, name, id }) =>
      services.some(
        ({ name: serviceName, id: serviceId }) => serviceName === name.trim() && id !== serviceId,
      ),
  },
  {
    errorKey: 'duplicateVariableName',
    validate: ({ costVariables }) => {
      const names = costVariables.map(({ name }) => normalizeText(name));

      const unique = Array.from(new Set(names));

      return unique.length !== names.length;
    },
  },
  {
    errorKey: 'duplicateTagName',
    validate: ({ costVariables }) => {
      const names = costVariables.map(({ name }) => normalizeText(name));

      const unique = Array.from(new Set(names));

      return unique.length !== names.length;
    },
  },
  {
    errorKey: 'costCalculationsEmpty',
    validate: ({ costVariables, costCalculation }) => {
      const hasCurrency = costVariables.find(({ type }) => type === 'CURRENCY');

      if (!hasCurrency) return !!costCalculation.length;

      return false;
    },
  },
  {
    errorKey: 'costCalculationsEmptyRefs',
    validate: ({ costCalculation }) => {
      return !!costCalculation.find(({ base, value }) =>
        [base, value].filter(Boolean).some((value) => !value?.ref),
      );
    },
  },
  {
    errorKey: 'costCalculationsCurrencyRef',
    validate: (service) => {
      const calculationRef = getCalculationReference(service);

      const error = !calculationRef || !validadeCurrencyRef(calculationRef, service);

      return error;
    },
  },
  {
    errorKey: 'currencyNumberSumOperations',
    validate: (service) => {
      const { costCalculation } = service;

      const invalidOperations = costCalculation
        // make dynamic later
        .filter(({ operation }) => operation === 'SUM' || operation === 'SUBTRACT')
        .find(({ base, value }) => {
          const [baseRefHasCurrency, valueRefHasCurrency] = [base, value].map(
            (value) =>
              !!resolveReferences(value as CostCalculationEntry, service).find(
                ({ type }) => type === 'CURRENCY',
              ),
          );

          const errors = [
            baseRefHasCurrency && !valueRefHasCurrency,
            valueRefHasCurrency && !baseRefHasCurrency,
          ].some(Boolean);

          return errors;
        });

      return !!invalidOperations;
    },
  },
];

export function validateService(
  service: ServiceToValidate,
  t: TFunction,
): AddToastParams | undefined {
  const error = validations.find(({ validate }) => validate(service));

  if (error) return MESSAGES[error.errorKey](t);
}
